import { useState, useEffect, useCallback } from 'react'
import { AdminAlert, AdminComingSoon, isNotBuiltYet } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import { adminGetUsers, adminMuteUser, adminUnmuteUser, adminGetModerationHistory, adminDeletePost, adminDeleteComment } from '../../../services/admin'

function ModerationHistoryRow({ entry }) {
  const isMute = (entry.action ?? '').toLowerCase().includes('mute') && !(entry.action ?? '').toLowerCase().includes('unmute')
  const isUnmute = (entry.action ?? '').toLowerCase().includes('unmute')
  return (
    <div className="border-2 border-on-background/15 bg-surface p-4 flex items-start justify-between gap-3 flex-wrap">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 border-2 font-bold text-[10px] uppercase tracking-widest ${
            isMute ? 'border-error text-error bg-error/10' :
            isUnmute ? 'border-primary-container text-on-primary-container bg-primary-container/20' :
            'border-on-background/30 text-on-surface-variant'
          }`}>
            {entry.action ?? 'Action'}
          </span>
          <span className="text-[12px] font-bold truncate">{entry.targetUser?.username ?? entry.targetUserId ?? entry.userId ?? 'Unknown user'}</span>
        </div>
        {entry.reason && <p className="text-[12px] text-on-surface-variant mt-1">{entry.reason}</p>}
        {entry.performedBy && (
          <p className="text-[11px] text-on-surface-variant font-mono mt-1">By: {entry.performedBy?.email ?? entry.performedBy}</p>
        )}
      </div>
      <span className="text-[11px] text-on-surface-variant font-mono shrink-0">
        {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ''}
      </span>
    </div>
  )
}

export function CommunityModeration() {
  const [search, setSearch] = useState('')
  const [users, setUsers] = useState([])
  const [meta, setMeta] = useState({ total: 0, page: 1, totalPages: 1, nextPage: null, prevPage: null })
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [recentAction, setRecentAction] = useState({})
  const [actingOn, setActingOn] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [muteTarget, setMuteTarget] = useState(null)
  const [muteDays, setMuteDays] = useState(7)
  const [muteReason, setMuteReason] = useState('')
  const [muteError, setMuteError] = useState(null)

  const openMute = (user) => { setMuteTarget(user); setMuteDays(7); setMuteReason(''); setMuteError(null) }
  const closeMute = () => { setMuteTarget(null); setMuteError(null) }

  const fetchUsers = (s = search, p = 1) => {
    if (!s.trim()) return
    setSearchLoading(true)
    setSearchError(null)
    setHasSearched(true)
    adminGetUsers({ search: s, filter: 'all', page: p })
      .then(res => {
        const list = Array.isArray(res?.data) ? res.data : []
        setUsers(list)
        setMeta(res?.meta ?? { total: list.length, page: p, totalPages: 1, nextPage: null, prevPage: null })
      })
      .catch(err => setSearchError(err))
      .finally(() => setSearchLoading(false))
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    fetchUsers(search, 1)
  }

  const handlePrev = () => { if (meta.prevPage) fetchUsers(search, meta.prevPage) }
  const handleNext = () => { if (meta.nextPage) fetchUsers(search, meta.nextPage) }

  const handleMuteToggle = (user, currentlyMuted) => {
    if (currentlyMuted) {
      handleUnmute(user)
    } else {
      openMute(user)
    }
  }

  const handleUnmute = async (user) => {
    setActingOn(user.id)
    setActionError(null)
    try {
      await adminUnmuteUser(user.id)
      setRecentAction(prev => ({ ...prev, [user.id]: 'unmuted' }))
      loadHistory()
    } catch (err) {
      setActionError(err.message ?? 'Failed to unmute user')
    } finally {
      setActingOn(null)
    }
  }

  const handleMuteConfirm = async () => {
    if (!muteReason.trim()) { setMuteError('Reason is required'); return }
    const days = Number(muteDays)
    if (!days || days <= 0) { setMuteError('Enter a valid number of days'); return }
    setActingOn(muteTarget.id)
    setMuteError(null)
    setActionError(null)
    try {
      await adminMuteUser(muteTarget.id, days, muteReason.trim())
      setRecentAction(prev => ({ ...prev, [muteTarget.id]: 'muted' }))
      closeMute()
      loadHistory()
    } catch (err) {
      setMuteError(err.message ?? 'Failed to mute user')
    } finally {
      setActingOn(null)
    }
  }

  const [history, setHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(true)
  const [historyError, setHistoryError] = useState(null)

  const loadHistory = useCallback(() => {
    setHistoryLoading(true)
    setHistoryError(null)
    adminGetModerationHistory()
      .then(setHistory)
      .catch(err => setHistoryError(err))
      .finally(() => setHistoryLoading(false))
  }, [])

  useEffect(() => { loadHistory() }, [loadHistory])

  if (historyError && isNotBuiltYet(historyError) && history.length === 0) {
    return <AdminComingSoon label="Community Moderation Tools" />
  }

  return (
    <div className="flex flex-col gap-8">
      {muteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4" onClick={e => { if (e.target === e.currentTarget) closeMute() }}>
          <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-5 p-6"
            style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
            <div className="flex items-center justify-between">
              <h2 className="font-extrabold text-[14px] uppercase tracking-widest">Mute User</h2>
              <button onClick={closeMute} className="p-1 hover:bg-surface-container transition-colors">
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>
            <p className="text-[12px] text-on-surface-variant">
              User: <span className="font-bold text-on-surface">{muteTarget.displayName || muteTarget.username || muteTarget.email}</span>
            </p>
            {muteError && <AdminAlert type="error" message={muteError} />}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Duration (days)</label>
                <div className="flex gap-2">
                  {[1, 3, 7, 30].map(d => (
                    <button key={d} type="button" onClick={() => setMuteDays(d)}
                      className={`flex-1 py-2 border-2 font-bold text-[11px] uppercase tracking-widest transition-colors ${Number(muteDays) === d ? 'bg-on-background text-surface border-on-background' : 'border-on-background hover:bg-surface-container'}`}>
                      {d}d
                    </button>
                  ))}
                </div>
                <input
                  type="number" min="1" placeholder="Custom days"
                  value={muteDays} onChange={e => setMuteDays(e.target.value)}
                  className="bg-surface border-2 border-on-background px-3 py-2 font-mono text-[14px] focus:outline-none focus:border-primary-container mt-1"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Reason <span className="text-error">*</span></label>
                <input
                  type="text" placeholder="e.g. Repeated spam posting"
                  value={muteReason} onChange={e => setMuteReason(e.target.value)}
                  className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={closeMute}
                className="flex-1 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
                Cancel
              </button>
              <button onClick={handleMuteConfirm} disabled={actingOn === muteTarget.id}
                className="flex-1 py-2.5 bg-error text-on-error font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50">
                {actingOn === muteTarget.id ? 'Muting…' : 'Mute User'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Mute / unmute a user ── */}
      <div className="flex flex-col gap-4">
        <div>
          <h3 className="font-extrabold text-[14px] uppercase tracking-widest">Mute / Unmute User</h3>
          <p className="text-[12px] text-on-surface-variant mt-1">Search for a user to restrict or restore their ability to post and comment.</p>
        </div>

        <form onSubmit={handleSearchSubmit} className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by username, email, or name…"
            className="flex-1 min-w-[200px] bg-surface border-2 border-on-background px-3 py-2.5 text-[13px] focus:outline-none focus:border-primary-container"
          />
          <button type="submit" disabled={searchLoading || !search.trim()}
            className="px-5 py-2.5 border-4 border-on-background bg-on-background text-surface font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}>
            {searchLoading ? 'Searching…' : 'Search'}
          </button>
        </form>

        {actionError && <AdminAlert type="error" message={actionError} />}
        {searchError && !isNotBuiltYet(searchError) && (
          <AdminAlert type="error" message={searchError.message ?? 'Failed to search users'} />
        )}

        {searchLoading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded" />)}
          </div>
        ) : hasSearched && users.length === 0 ? (
          <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-8 text-center">
            <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No users found</p>
          </div>
        ) : users.length > 0 ? (
          <div className="flex flex-col gap-3">
            {users.map(u => {
              const currentlyMuted = recentAction[u.id] === 'muted'
              return (
                <div key={u.id} className="border-4 border-on-background bg-surface p-4 flex items-center justify-between gap-3 flex-wrap"
                  style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
                  <div className="min-w-0">
                    <p className="font-bold text-[13px] truncate">{u.displayName || u.username || u.email}</p>
                    <p className="text-[11px] text-on-surface-variant font-mono truncate">{u.email}</p>
                    {recentAction[u.id] && (
                      <span className={`inline-block mt-1 px-2 py-0.5 border font-bold text-[10px] uppercase tracking-widest ${
                        currentlyMuted ? 'border-error text-error' : 'border-primary-container text-on-primary-container'
                      }`}>
                        {currentlyMuted ? 'Muted (this session)' : 'Unmuted (this session)'}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleMuteToggle(u, currentlyMuted)}
                    disabled={actingOn === u.id}
                    className={`px-4 py-2 border-2 font-bold text-[11px] uppercase tracking-widest transition-colors shrink-0 disabled:opacity-50 ${
                      currentlyMuted
                        ? 'border-primary-container text-on-primary-container hover:bg-primary-container hover:text-on-primary-fixed'
                        : 'border-error text-error hover:bg-error hover:text-on-error'
                    }`}
                  >
                    {actingOn === u.id ? 'Working…' : currentlyMuted ? 'Unmute' : 'Mute'}
                  </button>
                </div>
              )
            })}
            <div className="flex items-center justify-between pt-2">
              <p className="text-[11px] text-on-surface-variant font-mono">
                Page {meta.page} of {meta.totalPages || 1} · {meta.total} result{meta.total === 1 ? '' : 's'}
              </p>
              <div className="flex gap-2">
                <button onClick={handlePrev} disabled={!meta.prevPage || searchLoading}
                  className="px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-on-background hover:text-surface transition-colors disabled:opacity-40">Prev</button>
                <button onClick={handleNext} disabled={!meta.nextPage || searchLoading}
                  className="px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-on-background hover:text-surface transition-colors disabled:opacity-40">Next</button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* ── Moderation history ── */}
      <div className="flex flex-col gap-3">
        <h3 className="font-extrabold text-[14px] uppercase tracking-widest">Moderation History</h3>
        {historyError && !isNotBuiltYet(historyError) && (
          <AdminAlert type="error" message={historyError.message ?? 'Failed to load moderation history'} />
        )}
        {historyLoading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded" />)}
          </div>
        ) : history.length === 0 ? (
          <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-8 text-center">
            <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No moderation actions yet</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {history.map((entry, i) => (
              <ModerationHistoryRow key={entry.id ?? i} entry={entry} />
            ))}
          </div>
        )}
      </div>

      {/* ── Delete post / comment ── */}
      <ContentRemoval />
    </div>
  )
}

function ContentRemoval() {
  const [type, setType] = useState('post') // 'post' | 'comment'
  const [inputId, setInputId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const handleDelete = async (e) => {
    e.preventDefault()
    const id = inputId.trim()
    if (!id) { setError('Enter a valid ID'); return }
    setLoading(true)
    setError(null)
    setSuccessMsg(null)
    try {
      if (type === 'post') await adminDeletePost(id)
      else await adminDeleteComment(id)
      setSuccessMsg(`${type === 'post' ? 'Post' : 'Comment'} ${id} deleted successfully`)
      setInputId('')
    } catch (err) {
      setError(err.message ?? `Failed to delete ${type}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="font-extrabold text-[14px] uppercase tracking-widest">Remove Content</h3>
        <p className="text-[12px] text-on-surface-variant mt-1">
          Delete a specific post or comment by its ID. This action is permanent and cannot be undone.
        </p>
      </div>

      {error && <AdminAlert type="error" message={error} />}
      {successMsg && <AdminAlert type="success" message={successMsg} />}

      <form onSubmit={handleDelete} className="flex flex-col gap-3">
        <div className="flex gap-1 border-4 border-on-background w-fit">
          {['post', 'comment'].map(t => (
            <button
              key={t}
              type="button"
              onClick={() => { setType(t); setInputId(''); setError(null); setSuccessMsg(null) }}
              className={`px-4 py-2 font-bold text-[11px] uppercase tracking-widest transition-colors ${
                type === t ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={inputId}
            onChange={e => setInputId(e.target.value)}
            placeholder={`Enter ${type} UUID…`}
            className="flex-1 min-w-[240px] bg-surface border-2 border-on-background px-3 py-2.5 font-mono text-[13px] focus:outline-none focus:border-error"
          />
          <button
            type="submit"
            disabled={loading || !inputId.trim()}
            className="px-5 py-2.5 border-4 border-error bg-error text-on-error font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ boxShadow: '3px 3px 0px 0px var(--neo-border-color)' }}
          >
            {loading ? 'Deleting…' : `Delete ${type}`}
          </button>
        </div>
      </form>
    </div>
  )
}